import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { applyTenantFromHost } from "../lib/api";
import { clearAdminSession, getStoredAdminUser } from "../lib/auth";
import { UploadQueueProvider, UploadQueuePanel } from "../components/UploadQueue";
import "./admin.css";

const NAV = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/rooms", label: "Spaces", end: false },
  { to: "/alerts", label: "Alerts", end: false },
  { to: "/privacy", label: "Privacy", end: false },
];

export function AdminShell({ children }: { children?: ReactNode }) {
  const [tenant, setTenant] = useState("spielmann");
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = getStoredAdminUser();

  useEffect(() => {
    setTenant(applyTenantFromHost());
  }, []);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = navOpen ? "hidden" : "";
    if (!navOpen) return;
    const nav = document.getElementById("admin-nav");
    const prev = document.activeElement as HTMLElement | null;
    const focusables = () =>
      [...(nav?.querySelectorAll<HTMLElement>("button,[href],input,select,textarea,[tabindex]:not([tabindex='-1'])") ?? [])].filter(
        (el) => !el.hasAttribute("disabled"),
      );
    focusables()[0]?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setNavOpen(false);
        return;
      }
      if (e.key !== "Tab" || !nav) return;
      const list = focusables();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, [navOpen]);

  void tenant;

  const topbarTitle = (() => {
    if (location.pathname === "/") return "Dashboard";
    if (location.pathname === "/rooms") return "Spaces";
    if (location.pathname === "/alerts") return "Alerts";
    if (location.pathname.startsWith("/rooms/")) return "Space";
    if (location.pathname === "/privacy") return "Privacy";
    return "Dataroom";
  })();

  const showBack = location.pathname.startsWith("/rooms/") && location.pathname !== "/rooms";

  function signOut() {
    clearAdminSession();
    navigate("/login", { replace: true });
  }

  return (
    <UploadQueueProvider>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <div className={`admin-shell${navOpen ? " nav-open" : ""}`}>
        {navOpen ? (
          <button
            type="button"
            className="sidebar-backdrop"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
          />
        ) : null}
        <aside className="sidebar" id="admin-nav" aria-label="Primary">
          <div className="brand">
            <img className="brand-logo" src="/brand/spielmann-logo.svg" alt="Spielmann Consulting" />
            <span className="brand-tag">Dataroom</span>
          </div>
          <nav>
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => (isActive ? "nav active" : "nav")}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="sidebar-footer">
            <div className="sidebar-user" title={user?.email ?? ""}>
              <span className="sidebar-user__label">Signed in</span>
              <span className="sidebar-user__email">{user?.email ?? "Admin"}</span>
            </div>
            <button type="button" className="secondary sidebar-signout" onClick={signOut}>
              Sign out
            </button>
          </div>
        </aside>
        <main className="main" id="main-content">
          <div className="admin-topbar">
            <button
              type="button"
              className="menu-toggle"
              aria-expanded={navOpen}
              aria-controls="admin-nav"
              onClick={() => setNavOpen((v) => !v)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
              <span className="sr-only">Menu</span>
            </button>
            {showBack ? (
              <button type="button" className="ghost admin-topbar__back" onClick={() => navigate("/rooms")}>
                ← Spaces
              </button>
            ) : null}
            <span className="admin-topbar__title">{topbarTitle}</span>
            <img className="brand-logo-inline" src="/brand/spielmann-logo.svg" alt="Spielmann Consulting" />
          </div>
          {children ?? <Outlet />}
        </main>
        <UploadQueuePanel />
      </div>
    </UploadQueueProvider>
  );
}
